import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import './ProductList.css';
import { useCart } from './CartContext';
import CartSummary from './CartSummary';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function ProductList({ selectedCategory, searchTerm}) {
    const [products, setProducts] = useState([]); // 상품 목록 저장
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1); // 페이지 번호 관리
    const [hasMore, setHasMore] = useState(true); // 상품이 더 있는지 확인
    const [selectedProducts, setSelectedProducts] = useState([]); // 선택된 상품 저장
    const [loading, setLoading] = useState(false); // 로딩
    const { addToCart, removeFromCart } = useCart(); // 장바구니에 상품 추가, 삭제
    const history = useHistory();

    const urlParams = new URLSearchParams(window.location.search);
    const session = urlParams.get('session');
    const name = urlParams.get('name');

    useEffect(() => {
        setProducts([]);
        setPage(1);
        setHasMore(true);
        if (searchTerm) {
            fetchSearchResults(searchTerm, 1);
        } else {
            fetchProducts(selectedCategory, 1);
        }
    }, [selectedCategory, searchTerm]);

    useEffect(() => {
        if (hasMore && !searchTerm) {
            fetchProducts(selectedCategory, page);
        }
    }, [selectedCategory, page, searchTerm]);

    // 상품 로드
    const fetchProducts = async (category, page) => {
        setLoading(true); // 로딩 시작
        try {
            const categoryMap = {
                "전체": "all",
                "봉지 스낵": "봉지스낵",
                "갑 스낵": "갑스낵",
                "쿠키/파이": "쿠키파이",
                "젤리/초콜릿": "젤리초콜릿",
                "견과류/에너지바": "견과류에너지바",
                "음료": "음료",
                "우유/두유": "우유두유",
                "차/티백/커피": "차티백커피",
                "시리얼/컵라면": "시리얼컵라면"
            };
            const categoryPath = categoryMap[category] || "전체";
            const response = await fetch(`${API_BASE_URL}/api/cart/product/load/${categoryPath}/${page}`);

            if (!response.ok) {
                throw new Error('Network response was not ok ' + response.statusText);
            }
            const data = await response.json();
            const processedProducts = processProducts(data);
            setProducts(prevProducts => {
                const newProducts = processedProducts.filter(
                    newProduct => !prevProducts.some(product => product.id === newProduct.id)
                );
                return [...prevProducts, ...newProducts];
            });
            if (processedProducts.length === 0) setHasMore(false);
        } catch (error) {
            setError(error.message);
            console.error('Error fetching products:', error);
        } finally {
            setLoading(false); // 로딩 종료
        }
    };

    // 검색 상품 로드
    const fetchSearchResults = async (searchTerm, page) => {
        setLoading(true); // 로딩 시작
        try {
            // console.log(searchTerm)
            const response = await fetch(`${API_BASE_URL}/api/cart/product/search?keyword=${searchTerm}&page=${page}`);
            if (!response.ok) {
                throw new Error('Network response was not ok ' + response.statusText);
            }
            const data = await response.json();
            const processedProducts = processProducts(data);
            setProducts(prevProducts => {
                const newProducts = processedProducts.filter(
                    newProduct => !prevProducts.some(product => product.id === newProduct.id)
                );
                return [...prevProducts, ...newProducts];
            });
            if (processedProducts.length === 0) setHasMore(false);
        } catch (error) {
            setError(error.message);
            console.error('Error fetching search results:', error);
        } finally {
            setLoading(false); // 로딩 종료
        }
    };

    const processProducts = (productData) => {
        return productData.map(product => ({
            id: product.id,
            name: product.productName,
            price: product.displayPrice.toLocaleString(),
            perUnitPrice: product.perUnitPrice,
            // perUnitPrice: '10g 당 100원',
            image: product.imageUrl === 'https://img1a.coupangcdn.com/image/coupang/search/blank1x1.gif' 
                ? '/default.png' 
                : product.imageUrl,
            url: product.url,
            category: product.category
        }));
    };

    // 상품 선태 버튼
    const handleSelect = (product) => {
        setSelectedProducts((prevSelected) => {
            const isSelected = prevSelected.some(item => item.id === product.id);
            if (isSelected) {
                removeFromCart(product.id);
                return prevSelected.filter(item => item.id !== product.id);
            } else {
                addToCart(product);
                return [...prevSelected, product];
            }
        });
    };

    // 무한 스크롤
    const handleScroll = (e) => {
        const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
        if (bottom && hasMore) {
            setPage(prevPage => prevPage + 1);
        }
    };

    useEffect(() => {
        const container = document.querySelector('.product-list');
        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [hasMore]);

    // 상품 선택 완료
    const handleCompleteSelection = () => {
        setSelectedProducts([]);
        history.push(`/cart?session=${session}&name=${name}`);
    };

    const filteredProducts = searchTerm
    ? products.filter(product => 
        product.name.replace(/\s/g, '').toLowerCase().includes(searchTerm.replace(/\s/g, '').toLowerCase()))
    : products;


    return (
        <div className='real-container'>
        <div className="container">
            <div className="product-list">
                {loading ? (
                   <div></div>
                ) : error ? (
                    <div className="error-message">상품을 불러오는 데 문제가 발생했습니다.</div>
                ) : (
                    <div className="product-row">
                        {filteredProducts.length === 0 ? (
                            <div className="no-results-message">
                                검색어와 일치하는 상품이 없습니다.<br />내용을 다시 확인해주세요
                            </div>
                        ) : (
                            filteredProducts.map((product) => (
                                <div className={`product-card ${selectedProducts.some(item => item.id === product.id) ? 'selected' : ''}`} key={product.id}>
                                    <div className={`thumb ${selectedProducts.some(item => item.id === product.id) ? 'selected' : ''}`}
                                        onClick={() => handleSelect(product)}>
                                        <img src={product.image} alt={product.name} className="product-image" onError={(e) => e.target.src = '/default.png'}/>
                                        <button className={`select-button ${selectedProducts.some(item => item.id === product.id) ? 'selected' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleSelect(product)}}></button>
                                    </div>
                                    <div className="product-title">{product.name}</div>
                                    <div className='product-per-unit-price'>{product.perUnitPrice}</div>
                                    <div className="product-price">{product.price}원</div>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>
            
            <CartSummary
                selectedProducts={selectedProducts}
                onCompleteSelection={handleCompleteSelection}
                setSelectedProducts={setSelectedProducts} />
        </div>
        </div>
    );
}
export default ProductList;